import React, { useCallback, useState } from 'react';
import { CheckCircleIcon, XCircleIcon, AlertCircleIcon, ClockIcon, FileTextIcon, LockIcon, BrainCircuitIcon } from 'lucide-react';
type Invoice = {
  id: string;
  obligor: string;
  supplier: string;
  amount: number;
  dueDate: string;
  rating: string;
  tenor: number;
  status: 'pending' | 'ai_verified' | 'human_review' | 'approved' | 'flagged' | 'rejected';
  aiScore: number;
  riskFlags: string[];
  complianceChecks: {
    aml: 'pass' | 'fail' | 'pending';
    sanctions: 'pass' | 'fail' | 'pending';
    creditLimit: 'pass' | 'fail' | 'pending';
    concentration: 'pass' | 'fail' | 'pending';
  };
  documents: {
    invoice: boolean;
    po: boolean;
    bol: boolean;
    ucc: boolean;
  };
};
const INVOICES_DATA: Invoice[] = [{
  id: 'INV-10231',
  obligor: 'Walmart Inc.',
  supplier: 'PPI Apparel Group',
  amount: 165113,
  dueDate: '2025-12-28',
  rating: 'AA-',
  tenor: 45,
  status: 'ai_verified',
  aiScore: 94,
  riskFlags: [],
  complianceChecks: {
    aml: 'pass',
    sanctions: 'pass',
    creditLimit: 'pass',
    concentration: 'pass'
  },
  documents: {
    invoice: true,
    po: true,
    bol: true,
    ucc: false
  }
}, {
  id: 'INV-10232',
  obligor: 'Target Corp',
  supplier: 'Multi-Pak Packaging',
  amount: 243500,
  dueDate: '2025-12-15',
  rating: 'A+',
  tenor: 33,
  status: 'pending',
  aiScore: 88,
  riskFlags: ['High concentration'],
  complianceChecks: {
    aml: 'pass',
    sanctions: 'pass',
    creditLimit: 'pending',
    concentration: 'fail'
  },
  documents: {
    invoice: true,
    po: true,
    bol: false,
    ucc: false
  }
}, {
  id: 'INV-10233',
  obligor: 'Amazon',
  supplier: 'AGI Wholesale',
  amount: 512000,
  dueDate: '2026-01-10',
  rating: 'AA',
  tenor: 60,
  status: 'human_review',
  aiScore: 91,
  riskFlags: [],
  complianceChecks: {
    aml: 'pass',
    sanctions: 'pass',
    creditLimit: 'pass',
    concentration: 'pass'
  },
  documents: {
    invoice: true,
    po: true,
    bol: true,
    ucc: true
  }
}, {
  id: 'INV-10234',
  obligor: 'Home Depot',
  supplier: 'New South Construction Supply',
  amount: 89450,
  dueDate: '2025-12-10',
  rating: 'A',
  tenor: 30,
  status: 'pending',
  aiScore: 85,
  riskFlags: [],
  complianceChecks: {
    aml: 'pass',
    sanctions: 'pass',
    creditLimit: 'pass',
    concentration: 'pass'
  },
  documents: {
    invoice: true,
    po: true,
    bol: true,
    ucc: false
  }
}, {
  id: 'INV-10235',
  obligor: 'Costco',
  supplier: 'ZWILLING J.A. Henckels USA',
  amount: 178900,
  dueDate: '2025-12-31',
  rating: 'AA-',
  tenor: 45,
  status: 'ai_verified',
  aiScore: 92,
  riskFlags: [],
  complianceChecks: {
    aml: 'pass',
    sanctions: 'pass',
    creditLimit: 'pass',
    concentration: 'pass'
  },
  documents: {
    invoice: true,
    po: true,
    bol: true,
    ucc: true
  }
}];
export function Audit() {
  const [invoices, setInvoices] = useState<Invoice[]>(INVOICES_DATA);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(INVOICES_DATA[2]);
  const [liveMode, setLiveMode] = useState(true);
  const [verificationMode, setVerificationMode] = useState<'auto' | 'manual'>('auto');
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  const [flagNotes, setFlagNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [stats] = useState({
    totalProcessed: 1247,
    todayProcessed: 43,
    avgTime: 18,
    accuracyRate: 99.2,
    humanReview: 8.3
  });
  const [auditTrail, setAuditTrail] = useState([{
    time: '14:32:18',
    user: 'AI System',
    action: 'Auto-verified INV-10229',
    result: 'PASS'
  }, {
    time: '14:31:45',
    user: 'Sarah Chen',
    action: 'Manual review INV-10228',
    result: 'APPROVED'
  }, {
    time: '14:30:22',
    user: 'AI System',
    action: 'Flagged INV-10227',
    result: 'CONCENTRATION'
  }]);
  const handleApprove = useCallback(() => {
    setShowApprovalModal(false);
    if (selectedInvoice) {
      const now = new Date();
      const timeStr = now.toTimeString().slice(0, 8);
      setAuditTrail(prev => [{
        time: timeStr,
        user: 'Current User',
        action: `Approved ${selectedInvoice.id}`,
        result: 'APPROVED'
      }, ...prev]);
    }
  }, [selectedInvoice]);
  const handleFlag = useCallback(() => {
    setShowFlagModal(true);
  }, []);
  const handleFlagSubmit = useCallback(() => {
    if (!flagReason || !selectedInvoice) return;
    const now = new Date();
    const timeStr = now.toTimeString().slice(0, 8);
    const displayReason = flagReason === 'other' ? flagNotes : flagReason;
    setAuditTrail(prev => [{
      time: timeStr,
      user: 'Current User',
      action: `Flagged ${selectedInvoice.id}: ${displayReason}`,
      result: 'FLAGGED'
    }, ...prev]);
    setShowFlagModal(false);
    setFlagReason('');
    setFlagNotes('');
  }, [selectedInvoice, flagReason, flagNotes]);
  const handleReject = useCallback(() => {
    setShowRejectModal(true);
  }, []);
  const handleRejectSubmit = useCallback(() => {
    if (!rejectReason || !selectedInvoice) return;
    const now = new Date();
    const timeStr = now.toTimeString().slice(0, 8);
    setAuditTrail(prev => [{
      time: timeStr,
      user: 'Current User',
      action: `Rejected ${selectedInvoice.id}: ${rejectReason}`,
      result: 'REJECTED'
    }, ...prev]);
    setShowRejectModal(false);
    setRejectReason('');
  }, [selectedInvoice, rejectReason]);
  return <div className="min-h-screen bg-white" data-id="element-954">
      {/* Header - Larger and more prominent */}
      <div className="border-b border-gray-300 bg-white" data-id="element-955">
        <div className="px-6 py-6" data-id="element-956">
          <div className="flex items-center justify-between" data-id="element-957">
            <div className="flex items-center gap-6" data-id="element-958">
              <h1 className="text-3xl font-bold text-gray-900" data-id="element-959">Audit</h1>
              <div className="flex items-center gap-2" data-id="element-960">
                <div className={`w-2 h-2 rounded-full ${liveMode ? 'bg-gray-900' : 'bg-gray-400'}`} data-id="element-961" />
                <span className="text-xs text-gray-600 uppercase font-medium" data-id="element-962">
                  {liveMode ? 'LIVE' : 'PAUSED'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-963">
              <span className="px-4 py-2 bg-gray-900 text-white text-xs font-medium uppercase" data-id="element-964">
                MODE: {verificationMode.toUpperCase()}
              </span>
              <button onClick={() => setVerificationMode(v => v === 'auto' ? 'manual' : 'auto')} className="px-4 py-2 border border-gray-400 text-xs font-medium uppercase hover:bg-gray-50" data-id="element-965">
                SWITCH MODE
              </button>
              <button onClick={() => setLiveMode(!liveMode)} className="px-4 py-2 bg-gray-800 text-white text-xs font-medium uppercase hover:bg-gray-900" data-id="element-966">
                {liveMode ? 'STOP' : 'START'} LIVE
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Stats Bar - Bigger and more prominent */}
      <div className="bg-gray-800 text-white border-b-4 border-gray-900" data-id="element-967">
        <div className="px-6 py-6" data-id="element-968">
          <div className="grid grid-cols-5 gap-8" data-id="element-969">
            <div className="text-center" data-id="element-970">
              <div className="text-xs text-gray-400 uppercase mb-2 font-medium" data-id="element-971">
                TOTAL VERIFIED
              </div>
              <div className="text-3xl font-bold mb-1" data-id="element-972">
                {stats.totalProcessed.toLocaleString()}
              </div>
            </div>
            <div className="text-center" data-id="element-973">
              <div className="text-xs text-gray-400 uppercase mb-2 font-medium" data-id="element-974">
                TODAY
              </div>
              <div className="flex items-center justify-center gap-2" data-id="element-975">
                <span className="text-3xl font-bold" data-id="element-976">
                  {stats.todayProcessed}
                </span>
                <span className="text-sm text-green-400 font-medium" data-id="element-977">+12%</span>
              </div>
            </div>
            <div className="text-center" data-id="element-978">
              <div className="text-xs text-gray-400 uppercase mb-2 font-medium" data-id="element-979">
                AVG TIME
              </div>
              <div className="text-3xl font-bold mb-1" data-id="element-980">{stats.avgTime}s</div>
            </div>
            <div className="text-center" data-id="element-981">
              <div className="text-xs text-gray-400 uppercase mb-2 font-medium" data-id="element-982">
                ACCURACY
              </div>
              <div className="text-3xl font-bold mb-1" data-id="element-983">
                {stats.accuracyRate}%
              </div>
            </div>
            <div className="text-center" data-id="element-984">
              <div className="text-xs text-gray-400 uppercase mb-2 font-medium" data-id="element-985">
                HUMAN REVIEW
              </div>
              <div className="flex items-center justify-center gap-2" data-id="element-986">
                <span className="text-3xl font-bold" data-id="element-987">{stats.humanReview}%</span>
                <span className="text-sm text-green-400 font-medium" data-id="element-988">
                  -2.1%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="flex h-[calc(100vh-200px)]" data-id="element-989">
        {/* Left Panel */}
        <div className="w-1/2 border-r border-gray-300 overflow-y-auto" data-id="element-990">
          {/* Queue Header */}
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-200" data-id="element-991">
            <div className="flex items-center justify-between" data-id="element-992">
              <h2 className="text-xs font-bold uppercase" data-id="element-993">
                VERIFICATION QUEUE
              </h2>
              <div className="flex items-center gap-2" data-id="element-994">
                <span className="text-[10px] text-gray-600" data-id="element-995">3 PENDING</span>
                <span className="text-[10px] text-gray-600" data-id="element-996">2 AI VERIFIED</span>
                <span className="text-[10px] text-gray-600" data-id="element-997">1 IN REVIEW</span>
              </div>
            </div>
          </div>
          {/* Invoice List */}
          <div data-id="element-998">
            {invoices.map(invoice => <div key={invoice.id} onClick={() => setSelectedInvoice(invoice)} className={`px-6 py-4 border-b border-gray-100 cursor-pointer transition-all ${selectedInvoice?.id === invoice.id ? 'bg-gray-100 border-l-4 border-l-gray-900' : 'hover:bg-gray-50'}`} data-id="element-999">
                <div className="flex items-center gap-3 mb-2" data-id="element-1000">
                  <span className="text-sm font-bold" data-id="element-1001">{invoice.id}</span>
                  <span className="px-2 py-0.5 bg-gray-900 text-white text-[9px] font-bold" data-id="element-1002">
                    {invoice.rating}
                  </span>
                  <span className="px-2 py-0.5 bg-gray-600 text-white text-[9px]" data-id="element-1003">
                    AI: {invoice.aiScore}%
                  </span>
                  <span className={`px-2 py-0.5 text-[9px] font-medium ${invoice.status === 'ai_verified' ? 'bg-gray-200 text-gray-700' : invoice.status === 'pending' ? 'bg-gray-100 text-gray-600' : invoice.status === 'human_review' ? 'bg-gray-300 text-gray-800' : 'bg-gray-100 text-gray-600'}`} data-id="element-1004">
                    {invoice.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-[11px] text-gray-600" data-id="element-1005">
                  <div data-id="element-1006">
                    <span className="text-gray-400" data-id="element-1007">OBLIGOR:</span>{' '}
                    {invoice.obligor}
                  </div>
                  <div data-id="element-1008">
                    <span className="text-gray-400" data-id="element-1009">AMOUNT:</span> $
                    {invoice.amount.toLocaleString()}
                  </div>
                  <div data-id="element-1010">
                    <span className="text-gray-400" data-id="element-1011">SUPPLIER:</span>{' '}
                    {invoice.supplier}
                  </div>
                  <div data-id="element-1012">
                    <span className="text-gray-400" data-id="element-1013">TENOR:</span>{' '}
                    {invoice.tenor} days
                  </div>
                </div>
                {invoice.riskFlags.length > 0 && <div className="mt-2 text-[10px] text-gray-700" data-id="element-1014">
                    ⚠ {invoice.riskFlags.join(', ')}
                  </div>}
              </div>)}
          </div>
          {/* Compliance Matrix - More subtle */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" data-id="element-1015">
            <h3 className="text-xs font-bold uppercase mb-3" data-id="element-1016">
              REAL-TIME COMPLIANCE MATRIX
            </h3>
            <div className="grid grid-cols-4 gap-3" data-id="element-1017">
              <div className="text-center p-3 bg-white border border-gray-300" data-id="element-1018">
                <div className="text-[10px] text-gray-600 uppercase" data-id="element-1019">
                  AML/KYC
                </div>
                <div className="text-xl font-bold" data-id="element-1020">42</div>
                <div className="text-[9px] text-gray-600 uppercase" data-id="element-1021">PASS</div>
              </div>
              <div className="text-center p-3 bg-white border border-gray-300" data-id="element-1022">
                <div className="text-[10px] text-gray-600 uppercase" data-id="element-1023">
                  SANCTIONS
                </div>
                <div className="text-xl font-bold" data-id="element-1024">42</div>
                <div className="text-[9px] text-gray-600 uppercase" data-id="element-1025">PASS</div>
              </div>
              <div className="text-center p-3 bg-gray-50 border border-gray-400" data-id="element-1026">
                <div className="text-[10px] text-gray-600 uppercase" data-id="element-1027">
                  CREDIT LIMITS
                </div>
                <div className="text-xl font-bold" data-id="element-1028">39</div>
                <div className="text-[9px] text-gray-700 uppercase font-medium" data-id="element-1029">
                  WARNING
                </div>
              </div>
              <div className="text-center p-3 bg-gray-100 border border-gray-500" data-id="element-1030">
                <div className="text-[10px] text-gray-600 uppercase" data-id="element-1031">
                  CONCENTRATION
                </div>
                <div className="text-xl font-bold" data-id="element-1032">35</div>
                <div className="text-[9px] text-gray-800 uppercase font-medium" data-id="element-1033">
                  FAIL
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Right Panel */}
        <div className="w-1/2 flex flex-col" data-id="element-1034">
          {selectedInvoice && <>
              {/* Verification Interface */}
              <div className="flex-1 overflow-y-auto" data-id="element-1035">
                <div className="px-6 py-3 bg-gray-800 text-white" data-id="element-1036">
                  <div className="flex items-center justify-between" data-id="element-1037">
                    <h2 className="text-xs font-bold uppercase" data-id="element-1038">
                      VERIFICATION INTERFACE
                    </h2>
                    <button className="text-white hover:text-gray-300" data-id="element-1039">
                      ✕
                    </button>
                  </div>
                </div>
                <div className="p-6 space-y-6" data-id="element-1040">
                  {/* Invoice Header */}
                  <div data-id="element-1041">
                    <div className="flex items-center justify-between mb-4" data-id="element-1042">
                      <span className="text-lg font-bold" data-id="element-1043">
                        {selectedInvoice.id}
                      </span>
                      <div className="flex items-center gap-2" data-id="element-1044">
                        <span className="px-2 py-1 bg-gray-900 text-white text-[10px] font-bold" data-id="element-1045">
                          {selectedInvoice.rating}
                        </span>
                        <span className="px-2 py-1 bg-gray-600 text-white text-[10px]" data-id="element-1046">
                          AI: {selectedInvoice.aiScore}%
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-1047">
                      <div data-id="element-1048">
                        <span className="text-gray-500" data-id="element-1049">Obligor:</span>
                        <div className="font-semibold" data-id="element-1050">
                          {selectedInvoice.obligor}
                        </div>
                      </div>
                      <div data-id="element-1051">
                        <span className="text-gray-500" data-id="element-1052">Supplier:</span>
                        <div className="font-semibold" data-id="element-1053">
                          {selectedInvoice.supplier}
                        </div>
                      </div>
                      <div data-id="element-1054">
                        <span className="text-gray-500" data-id="element-1055">Amount:</span>
                        <div className="font-semibold" data-id="element-1056">
                          ${selectedInvoice.amount.toLocaleString()}
                        </div>
                      </div>
                      <div data-id="element-1057">
                        <span className="text-gray-500" data-id="element-1058">Due Date:</span>
                        <div className="font-semibold" data-id="element-1059">
                          {selectedInvoice.dueDate}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Compliance Checks - Minimal design */}
                  <div data-id="element-1060">
                    <h3 className="text-[11px] font-bold text-gray-700 uppercase mb-3" data-id="element-1061">
                      COMPLIANCE CHECKS
                    </h3>
                    <div className="grid grid-cols-2 gap-2" data-id="element-1062">
                      {Object.entries(selectedInvoice.complianceChecks).map(([key, status]) => <div key={key} className={`px-3 py-2 border text-[11px] font-medium uppercase ${status === 'pass' ? 'bg-white border-gray-300 text-gray-700' : status === 'pending' ? 'bg-gray-50 border-gray-300 text-gray-500' : 'bg-gray-100 border-gray-400 text-gray-800'}`} data-id="element-1063">
                            <span className="mr-2" data-id="element-1064">
                              {status === 'pass' ? '✓' : status === 'pending' ? '○' : '✗'}
                            </span>
                            {key.replace(/([A-Z])/g, ' $1').toUpperCase()}
                          </div>)}
                    </div>
                  </div>
                  {/* Document Verification - Minimal */}
                  <div data-id="element-1065">
                    <h3 className="text-[11px] font-bold text-gray-700 uppercase mb-3" data-id="element-1066">
                      DOCUMENT VERIFICATION
                    </h3>
                    <div className="grid grid-cols-2 gap-2" data-id="element-1067">
                      {Object.entries(selectedInvoice.documents).map(([key, present]) => <div key={key} className={`px-3 py-2 border text-[11px] font-medium uppercase ${present ? 'bg-white border-gray-300 text-gray-700' : 'bg-gray-50 border-gray-300 text-gray-400'}`} data-id="element-1068">
                            <span className="mr-2" data-id="element-1069">□</span>
                            {key.toUpperCase()}
                            {present && <span className="float-right" data-id="element-1070">✓</span>}
                          </div>)}
                    </div>
                  </div>
                  {/* AI Analysis - Professional */}
                  <div className="border border-gray-300 bg-gray-50 p-4" data-id="element-1071">
                    <div className="flex items-center gap-2 mb-2" data-id="element-1072">
                      <span className="text-[11px] font-bold text-gray-700 uppercase" data-id="element-1073">
                        AI ANALYSIS
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1074">
                      Invoice verified with {selectedInvoice.aiScore}%
                      confidence. All compliance checks passed. Document
                      verification shows UCC filing pending. No risk flags
                      detected. Recommended for approval.
                    </p>
                  </div>
                  {/* Ready Status */}
                  {selectedInvoice.complianceChecks.aml === 'pass' && selectedInvoice.complianceChecks.sanctions === 'pass' && <div className="px-4 py-3 bg-white border border-gray-400" data-id="element-1075">
                        <span className="text-sm font-medium text-gray-800" data-id="element-1076">
                          ✓ Ready for Approval
                        </span>
                      </div>}
                  {/* Action Buttons - Professional */}
                  <div className="flex gap-3 pt-2" data-id="element-1077">
                    <button onClick={() => setShowApprovalModal(true)} className="flex-1 px-4 py-2.5 bg-gray-900 text-white text-xs font-bold uppercase hover:bg-gray-800" data-id="element-1078">
                      ✓ APPROVE
                    </button>
                    <button onClick={handleFlag} className="flex-1 px-4 py-2.5 border border-gray-400 text-gray-700 text-xs font-bold uppercase hover:bg-gray-50" data-id="element-1079">
                      ○ FLAG
                    </button>
                    <button onClick={handleReject} className="flex-1 px-4 py-2.5 border border-gray-400 text-gray-700 text-xs font-bold uppercase hover:bg-gray-50" data-id="element-1080">
                      ✕ REJECT
                    </button>
                  </div>
                  <div className="text-[10px] text-gray-500 text-center pt-3 border-t" data-id="element-1081">
                    All actions are logged and require Level 2 approval for
                    overrides
                  </div>
                </div>
              </div>
              {/* Audit Trail - Bottom */}
              <div className="border-t border-gray-300 bg-gray-50" data-id="element-1082">
                <div className="px-6 py-3" data-id="element-1083">
                  <h3 className="text-xs font-bold uppercase mb-3" data-id="element-1084">
                    AUDIT TRAIL
                  </h3>
                  <div className="space-y-1 max-h-32 overflow-y-auto" data-id="element-1085">
                    {auditTrail.map((entry, i) => <div key={i} className="flex items-center gap-4 text-[11px] font-mono" data-id="element-1086">
                        <span className="text-gray-500" data-id="element-1087">{entry.time}</span>
                        <span className="font-medium w-24" data-id="element-1088">{entry.user}</span>
                        <span className="flex-1 text-gray-600" data-id="element-1089">
                          {entry.action}
                        </span>
                        <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-[9px] font-semibold uppercase" data-id="element-1090">
                          {entry.result}
                        </span>
                      </div>)}
                  </div>
                </div>
              </div>
            </>}
        </div>
      </div>
      {/* Approval Modal */}
      {showApprovalModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-1091">
          <div className="bg-white border border-gray-400 shadow-xl max-w-md w-full" data-id="element-1092">
            <div className="px-6 py-4 border-b border-gray-300" data-id="element-1093">
              <h3 className="text-sm font-semibold" data-id="element-1094">Confirm Approval</h3>
            </div>
            <div className="px-6 py-4" data-id="element-1095">
              <p className="text-sm text-gray-700" data-id="element-1096">
                Are you sure you want to approve invoice {selectedInvoice?.id}?
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-300 flex justify-end gap-3" data-id="element-1097">
              <button onClick={() => setShowApprovalModal(false)} className="px-4 py-2 border border-gray-400 text-sm text-gray-700 hover:bg-gray-100" data-id="element-1098">
                Cancel
              </button>
              <button onClick={handleApprove} className="px-4 py-2 bg-gray-900 text-white text-sm hover:bg-gray-800" data-id="element-1099">
                Confirm
              </button>
            </div>
          </div>
        </div>}
      {/* Flag Modal */}
      {showFlagModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-1100">
          <div className="bg-white border border-gray-400 shadow-xl max-w-md w-full" data-id="element-1101">
            <div className="px-6 py-4 border-b border-gray-300" data-id="element-1102">
              <h3 className="text-sm font-semibold" data-id="element-1103">
                Flag Invoice {selectedInvoice?.id}
              </h3>
            </div>
            <div className="px-6 py-4" data-id="element-1104">
              <label className="block text-xs font-medium text-gray-700 mb-2 uppercase" data-id="element-1105">
                Flag Reason
              </label>
              <select value={flagReason} onChange={e => setFlagReason(e.target.value)} className="w-full px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:border-gray-500" data-id="element-1106">
                <option value="" data-id="element-1107">Select reason...</option>
                <option value="concentration" data-id="element-1108">Concentration Risk</option>
                <option value="verification" data-id="element-1109">Verification Failed</option>
                <option value="duplicate" data-id="element-1110">Duplicate Invoice</option>
                <option value="fraud" data-id="element-1111">Suspected Fraud</option>
                <option value="documentation" data-id="element-1112">Missing Documentation</option>
                <option value="other" data-id="element-1113">Other (specify below)</option>
              </select>
              <label className="block text-xs font-medium text-gray-700 mt-4 mb-2 uppercase" data-id="element-1114">
                Additional Notes
              </label>
              <textarea value={flagNotes} onChange={e => setFlagNotes(e.target.value)} placeholder="Enter additional details..." className="w-full px-3 py-2 border border-gray-300 text-sm h-20 focus:outline-none focus:border-gray-500" data-id="element-1115" />
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200" data-id="element-1116">
                <p className="text-xs text-amber-800" data-id="element-1117">
                  ⚠️ This will escalate to Level 2 review. SLA: 4 hours.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-300 flex justify-between" data-id="element-1118">
              <button onClick={() => {
            setShowFlagModal(false);
            setFlagReason('');
            setFlagNotes('');
          }} className="px-4 py-2 border border-gray-400 text-sm text-gray-700 hover:bg-gray-100" data-id="element-1119">
                Cancel
              </button>
              <button onClick={handleFlagSubmit} disabled={!flagReason} className="px-4 py-2 bg-amber-600 text-white text-sm hover:bg-amber-700 disabled:bg-gray-400 disabled:cursor-not-allowed" data-id="element-1120">
                Submit Flag
              </button>
            </div>
          </div>
        </div>}
      {/* Reject Modal */}
      {showRejectModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-1121">
          <div className="bg-white border border-gray-400 shadow-xl max-w-md w-full" data-id="element-1122">
            <div className="px-6 py-4 border-b border-gray-300" data-id="element-1123">
              <h3 className="text-sm font-semibold" data-id="element-1124">
                Reject Invoice {selectedInvoice?.id}
              </h3>
            </div>
            <div className="px-6 py-4" data-id="element-1125">
              <div className="space-y-3 mb-4" data-id="element-1126">
                <div className="flex justify-between text-sm" data-id="element-1127">
                  <span className="text-gray-600" data-id="element-1128">Obligor:</span>
                  <span className="font-medium" data-id="element-1129">
                    {selectedInvoice?.obligor}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-1130">
                  <span className="text-gray-600" data-id="element-1131">Amount:</span>
                  <span className="font-medium" data-id="element-1132">
                    ${selectedInvoice?.amount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-1133">
                  <span className="text-gray-600" data-id="element-1134">Supplier:</span>
                  <span className="font-medium" data-id="element-1135">
                    {selectedInvoice?.supplier}
                  </span>
                </div>
              </div>
              <label className="block text-xs font-medium text-gray-700 mb-2 uppercase" data-id="element-1136">
                Rejection Reason
              </label>
              <select value={rejectReason} onChange={e => setRejectReason(e.target.value)} className="w-full px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:border-gray-500" data-id="element-1137">
                <option value="" data-id="element-1138">Select reason...</option>
                <option value="failed_aml" data-id="element-1139">Failed AML Check</option>
                <option value="failed_sanctions" data-id="element-1140">Failed Sanctions Check</option>
                <option value="credit_limit" data-id="element-1141">Exceeds Credit Limit</option>
                <option value="invalid_documents" data-id="element-1142">Invalid Documentation</option>
                <option value="duplicate" data-id="element-1143">Duplicate Invoice</option>
                <option value="fraud" data-id="element-1144">Confirmed Fraud</option>
              </select>
              <div className="mt-4 p-3 bg-red-50 border border-red-200" data-id="element-1145">
                <p className="text-xs text-red-800 font-medium" data-id="element-1146">
                  ⚠️ WARNING: This action cannot be undone.
                </p>
                <p className="text-xs text-red-700 mt-1" data-id="element-1147">
                  Invoice will be permanently rejected and supplier will be
                  notified.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-300 flex justify-between" data-id="element-1148">
              <button onClick={() => {
            setShowRejectModal(false);
            setRejectReason('');
          }} className="px-4 py-2 border border-gray-400 text-sm text-gray-700 hover:bg-gray-100" data-id="element-1149">
                Cancel
              </button>
              <button onClick={handleRejectSubmit} disabled={!rejectReason} className="px-4 py-2 bg-red-600 text-white text-sm hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed" data-id="element-1150">
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>}
    </div>;
}